import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "@mui/material";
import styles from "./UpdateProfileDialog.module.css";
import { translate } from "../../../../common/providers";
import {
	CustomDialog,
	CustomButton,
	icon,
	createNotification,
	UserAvatar,
	clearProfilePictureCache,
	resetCurrentUserProfilePictureFetch,
	CustomTextField,
} from "../../../../common/components";
import { MyService, ApiService } from "../../../../api";
import {
	setUserProfilePicture,
	clearCurrentUserProfilePictureCache,
	updateUserInfo,
} from "../../../../common/slice/contextSlice";
import { ProfilePictureConsentDialog } from "../profile-picture-consent-dialog";

export default function UpdateProfileDialog({
	open,
	onClose,
	user,
	profilePicture,
	onDeletePhoto,
	isProfilePictureEnabled = true,
}) {
	const dispatch = useDispatch();
	const currentUser = useSelector(({ context }) => context.user);
	const fileInputRef = useRef(null);
	const cancelTokenSourceRef = useRef(null);
	const [isSaving, setIsSaving] = useState(false);
	const [showConsentDialog, setShowConsentDialog] = useState(false);
	const [payload, setPayload] = useState({
		firstname: "",
		lastname: "",
	});
	const [errors, setErrors] = useState({ firstname: false, lastname: false });
	const [selectedFile, setSelectedFile] = useState(null);
	const [previewUrl, setPreviewUrl] = useState(null);

	const displayName = useMemo(
		() => user?.displayName || `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || user?.email || "",
		[user]
	);
	const hasInfoChanged = useMemo(() => {
		if (!user) {
			return false;
		}
		return payload.firstname !== (user.firstName || "") || payload.lastname !== (user.lastName || "");
	}, [payload, user]);
	const reset = useCallback(() => {
		setErrors({ firstname: false, lastname: false });
		setIsSaving(false);
		if (previewUrl) {
			URL.revokeObjectURL(previewUrl);
		}
		setSelectedFile(null);
		setPreviewUrl(null);
	}, [previewUrl]);

	useEffect(() => {
		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		return () => {
			if (cancelTokenSourceRef.current) {
				ApiService.cancelTokens(cancelTokenSourceRef.current);
			}
		};
	}, []);
	useEffect(() => {
		if (user) {
			setPayload({ firstname: user.firstName || "", lastname: user.lastName || "" });
		}
	}, [user]);

	const handleSubmit = useCallback(
		async (event) => {
			if (event) {
				event.preventDefault();
			}
			const newErrors = {
				firstname: !payload.firstname,
				lastname: !payload.lastname,
			};
			if (newErrors.firstname || newErrors.lastname) {
				setErrors(newErrors);
				return;
			}
			if (!hasInfoChanged && !selectedFile) {
				onClose();
				return;
			}
			setIsSaving(true);
			try {
				if (selectedFile) {
					await MyService.uploadProfilePicture(selectedFile, cancelTokenSourceRef.current?.token);
					dispatch(clearCurrentUserProfilePictureCache());
					clearProfilePictureCache();
					resetCurrentUserProfilePictureFetch();
					dispatch(setUserProfilePicture(previewUrl));
					setSelectedFile(null);
					setPreviewUrl(null);
				}
				if (hasInfoChanged) {
					dispatch(
						updateUserInfo({
							firstName: payload.firstname,
							lastName: payload.lastname,
						})
					);

					await MyService.changeInformation(payload, cancelTokenSourceRef.current?.token);
				}
				createNotification({
					type: "success",
					message: translate("profile.update-profile.success"),
				});
				onClose();
			} catch (err) {
				console.error(err);
				if (hasInfoChanged) {
					dispatch(
						updateUserInfo({
							firstName: user.firstName,
							lastName: user.lastName,
						})
					);
				}
			} finally {
				setIsSaving(false);
			}
		},
		[dispatch, hasInfoChanged, onClose, payload, user, selectedFile, previewUrl]
	);
	const handleChange = useCallback((field, value) => {
		if (!field) {
			return;
		}
		setErrors((prev) => ({ ...prev, [field]: !value }));
		setPayload((prev) => ({ ...prev, [field]: value }));
	}, []);
	const handleFileSelect = useCallback(
		(event) => {
			const file = event.target.files?.[0];
			if (!file) {
				return;
			}

			const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
			if (!validTypes.includes(file.type)) {
				createNotification({
					type: "error",
					message: translate("profile.profile-picture.error.invalid-format"),
				});
				return;
			}

			const maxSize = 5 * 1024 * 1024;
			if (file.size > maxSize) {
				createNotification({
					type: "error",
					message: translate("profile.profile-picture.error.file-too-large"),
				});
				return;
			}

			if (previewUrl) {
				URL.revokeObjectURL(previewUrl);
			}

			const localImageUrl = URL.createObjectURL(file);
			setSelectedFile(file);
			setPreviewUrl(localImageUrl);

			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
		},
		[previewUrl]
	);
	const handleAddPhoto = useCallback(() => {
		if (!isProfilePictureEnabled) {
			return;
		}
		setShowConsentDialog(true);
	}, [isProfilePictureEnabled]);
	const handleConsentAccepted = useCallback(() => {
		setShowConsentDialog(false);
		fileInputRef.current?.click();
	}, []);
	const handleCloseConsentDialog = useCallback(() => {
		setShowConsentDialog(false);
	}, []);
	const handleDeletePhoto = useCallback(() => {
		if (previewUrl) {
			URL.revokeObjectURL(previewUrl);
			setPreviewUrl(null);
			setSelectedFile(null);
		}
		onDeletePhoto();
		onClose();
	}, [previewUrl, onDeletePhoto, onClose]);

	return (
		<>
			<CustomDialog
				disableBackdropClick
				displayCloseCross
				fullWidth
				isForm
				maxWidth="xs"
				open={open}
				submitDisabled={isSaving}
				title={translate("profile.update-profile.btn")}
				onClose={onClose}
				onExited={reset}
				onSubmit={handleSubmit}
			>
				<div className={styles.dialog__content}>
					<div className={styles.avatarSection}>
						<UserAvatar
							isCurrentUser
							className={styles.avatar}
							displayName={displayName}
							email={user?.email}
							firstName={user?.firstName}
							lastName={user?.lastName}
							size="xl"
							src={previewUrl}
							userId={currentUser?.id}
						/>
						<div className={styles.avatarActions}>
							<Tooltip
								arrow
								placement="bottom"
								title={
									!isProfilePictureEnabled
										? translate("profile.profile-picture.disabled-tooltip")
										: ""
								}
							>
								<span>
									<CustomButton
										color="secondary"
										disabled={isSaving || !isProfilePictureEnabled}
										size="sm"
										startIcon={icon.faPenToSquare}
										variant="outlined"
										onClick={handleAddPhoto}
									>
										{profilePicture || previewUrl
											? translate("profile.profile-picture.btn.edit")
											: translate("profile.profile-picture.btn.add")}
									</CustomButton>
								</span>
							</Tooltip>
							{(profilePicture || previewUrl) && (
								<CustomButton
									color="destructive"
									disabled={isSaving}
									size="sm"
									startIcon={icon.faTrash}
									variant="outlined"
									onClick={handleDeletePhoto}
								>
									{translate("common:btn.delete")}
								</CustomButton>
							)}
						</div>
						<input
							ref={fileInputRef}
							accept="image/jpeg,image/jpg,image/png,image/gif"
							className={styles["input--hidden"]}
							type="file"
							onChange={handleFileSelect}
						/>
					</div>
					<CustomTextField
						fullWidth
						required
						className={styles.dialog__field}
						error={errors.firstname}
						helperText={errors.firstname && translate("common:error.required")}
						label={translate("profile.update-profile.first-name")}
						size="small"
						value={payload.firstname}
						variant="outlined"
						onChange={(e) => handleChange("firstname", e.target.value)}
					/>
					<CustomTextField
						fullWidth
						required
						className={styles.dialog__field}
						error={errors.lastname}
						helperText={errors.lastname && translate("common:error.required")}
						label={translate("profile.update-profile.last-name")}
						size="small"
						value={payload.lastname}
						variant="outlined"
						onChange={(e) => handleChange("lastname", e.target.value)}
					/>
				</div>
			</CustomDialog>
			<ProfilePictureConsentDialog
				open={showConsentDialog}
				onClose={handleCloseConsentDialog}
				onConsent={handleConsentAccepted}
			/>
		</>
	);
}
